import { useEffect, useMemo, useState, useCallback, useRef, memo, useDeferredValue } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAppStore } from '@/stores/appStore'
import { api, Folder, FileWithVersion, Org, Team, Tag, SharedItem, DroppedItem, PublicSharedFileItem, PublicFileAccessLog } from '@/services/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  FolderOpen,
  File,
  Upload,
  FolderPlus,
  Search,
  ChevronRight,
  MoreVertical,
  Download,
  Trash2,
  Edit,
  Share2,
  Image,
  FileText,
  Music,
  Video,
  Archive,
  Building2,
  Users,
  User,
  Share,
  Home,
  ArrowUpDown,
  ArrowRightLeft,
  Tag as TagIcon,
  Check,
  Star,
  X,
  Loader2,
  RotateCw,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatBytes, formatDate, formatDateOnly, getFileIcon } from '@/lib/utils'
import { toast } from '@/components/ui/use-toast'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PDFViewer } from '@/components/PDFViewer'
import { MarkdownViewer } from '@/components/MarkdownViewer'
import { PDFThumbnail } from '@/components/PDFThumbnail'
import { ShareModal } from '@/components/ShareModal'

function isMarkdownFile(file: FileWithVersion): boolean {
  const mime = (file.current_version?.mime_type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()

  if (name.endsWith('.md') || name.endsWith('.markdown') || name.endsWith('.mdown') || name.endsWith('.mkd') || name.endsWith('.mdx')) {
    return true
  }

  if (mime.includes('markdown') || mime === 'text/x-markdown') {
    return true
  }

  // Many markdown uploads are stored as text/plain by browsers/storage.
  return mime.startsWith('text/')
}

const fileIcons: Record<string, typeof File> = {
  image: Image,
  pdf: FileText,
  document: FileText,
  spreadsheet: FileText,
  presentation: FileText,
  audio: Music,
  video: Video,
  archive: Archive,
  file: File,
}

const fileIconColors: Record<string, string> = {
  image: 'text-emerald-500',
  pdf: 'text-red-500',
  document: 'text-blue-500',
  spreadsheet: 'text-green-600',
  presentation: 'text-orange-500',
  audio: 'text-violet-500',
  video: 'text-pink-500',
  archive: 'text-amber-500',
  file: 'text-muted-foreground',
}

interface TradingCardThumbProps {
  file: FileWithVersion
  orgId: string | null
  onOpenPdf: () => void
  onOpenImage: () => void
  onOpenMarkdown: () => void
}

interface InlineImageThumbProps {
  file: FileWithVersion
  orgId: string
  className?: string
}

const InlineImageThumb = memo(({ file, orgId, className = '' }: InlineImageThumbProps) => {
  const [url, setUrl] = useState<string | null>(null)

  const isImage = file.current_version?.mime_type?.startsWith('image/')

  useEffect(() => {
    if (!isImage) return
    let cancelled = false
    let blobUrl: string | null = null

    api.files.thumbnail.getBlob(orgId, file.id, 'small')
      .then((blob) => {
        if (cancelled) return
        if (blob) {
          blobUrl = window.URL.createObjectURL(blob)
          setUrl(blobUrl)
        }
      })
      .catch(() => { /* silently show placeholder */ })

    return () => {
      cancelled = true
      if (blobUrl) window.URL.revokeObjectURL(blobUrl)
    }
  }, [file.id, orgId, isImage])

  return (
    <div className={`relative bg-muted ${className}`}>
      {url ? (
        <img
          src={url}
          alt={file.name}
          className="absolute inset-0"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <Image className="h-10 w-10 text-muted-foreground" />
        </div>
      )}
    </div>
  )
})

InlineImageThumb.displayName = 'InlineImageThumb'

const TradingCardThumb = memo(({ file, orgId, onOpenPdf, onOpenImage, onOpenMarkdown }: TradingCardThumbProps) => {
  const isPdf = file.current_version?.mime_type === 'application/pdf'
  const isImage = file.current_version?.mime_type?.startsWith('image/')
  const isMarkdown = isMarkdownFile(file)
  const canInlineImage = !!isImage && !!(orgId || file.org_id)

  const effectiveOrgId = orgId || file.org_id

  if (isPdf && effectiveOrgId) {
    return (
      <button type="button" onClick={onOpenPdf} className="block w-full focus:outline-none">
        <PDFThumbnail orgId={effectiveOrgId} fileId={file.id} width={360} height={460} className="w-full" />
      </button>
    )
  }

  const iconType = file.current_version ? getFileIcon(file.current_version.mime_type) : 'file'
  const Icon = fileIcons[iconType] || File

  const handleClick = isPdf ? onOpenPdf : isImage ? onOpenImage : isMarkdown ? onOpenMarkdown : undefined

  return (
    <div
      className={`w-full aspect-[3/4] bg-muted flex items-center justify-center rounded-md border ${
        handleClick ? 'cursor-pointer' : ''
      }`}
      onClick={handleClick}
    >
      {canInlineImage && effectiveOrgId ? (
        <InlineImageThumb file={file} orgId={effectiveOrgId} className="w-full h-full rounded-md" />
      ) : (
        <Icon className="h-14 w-14 text-muted-foreground" />
      )}
    </div>
  )
})

TradingCardThumb.displayName = 'TradingCardThumb'

type SecurityDomain = 
  | { type: 'my-files' }
  | { type: 'org'; org: Org }
  | { type: 'team'; team: Team; org: Org }
  | { type: 'shared' }
  | { type: 'public-shared' }

interface DomainFolder {
  id: string
  name: string
  icon: typeof FolderOpen
  iconColor: string
  description: string
  domain: SecurityDomain
}

export function FilesPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, orgs, currentFolder, folderContents, fetchFolderContents, isLoading, setCurrentOrg } =
    useAppStore()
  const [searchQuery, setSearchQuery] = useState('')
  // Live filter over the folder contents that are already loaded; nothing hits the server.
  const deferredSearchQuery = useDeferredValue(searchQuery)
  const filterTerms = useMemo(
    () => deferredSearchQuery.toLowerCase().split(/\s+/).filter(Boolean),
    [deferredSearchQuery],
  )
  const isFiltering = filterTerms.length > 0
  const [activeDomain, setActiveDomain] = useState<SecurityDomain | null>(null)
  const [allTeams, setAllTeams] = useState<{ team: Team; org: Org }[]>([])
	const [isUploading, setIsUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [sortKey] = useState<'name' | 'size' | 'created_at' | 'updated_at'>('name')
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
	const [viewMode, setViewMode] = useState<'standard' | 'minimal' | 'trading'>(() => {
		if (typeof window === 'undefined') return 'standard'
		const stored = window.localStorage.getItem('filesViewMode')
		if (stored === 'minimal' || stored === 'trading' || stored === 'standard') return stored
		return 'standard'
	})
	const [isRefreshing, setIsRefreshing] = useState(false)
	const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set())
	const [favoritesFiles, setFavoritesFiles] = useState<FileWithVersion[]>([])
	const [isFavoritesView, setIsFavoritesView] = useState(false)

  const [sharedItems, setSharedItems] = useState<Array<SharedItem & { orgId: string; orgName: string }>>([])
  const [droppedItems, setDroppedItems] = useState<Array<DroppedItem & { orgId: string; orgName: string }>>([])
  const [sharedLoading, setSharedLoading] = useState(false)
  const [publicSharedItems, setPublicSharedItems] = useState<Array<PublicSharedFileItem & { orgId: string; orgName: string }>>([])
  const [publicSharedLoading, setPublicSharedLoading] = useState(false)
  const [publicAccessLogs, setPublicAccessLogs] = useState<PublicFileAccessLog[]>([])
  const [publicAccessLogsOpen, setPublicAccessLogsOpen] = useState(false)
  const [publicAccessLogsTitle, setPublicAccessLogsTitle] = useState('Public Access Logs')

  // Tag management state
  const [availableTags, setAvailableTags] = useState<Tag[]>([])
  const [taggingFile, setTaggingFile] = useState<FileWithVersion | null>(null)
  const [filterTagId, setFilterTagId] = useState<string | null>(null)

  // Persist a stable org for "My Files" so creating new orgs doesn't move data
  const [myFilesOrgId, setMyFilesOrgId] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    const stored = window.localStorage.getItem('myFilesOrgId')
    return stored || null
  })
  const [resolvingMyFilesOrg, setResolvingMyFilesOrg] = useState(false)

  // Choose an org for "My Files" (prefer owner role, fallback first org)
  const getMyFilesOrgId = useCallback((): string | null => {
    if (myFilesOrgId) return myFilesOrgId
    const ntgOrg = orgs.find((o) => o.name?.toLowerCase() === 'ntg')
    if (ntgOrg) return ntgOrg.id
    const ownerOrg = orgs.find((o) => o.role === 'owner')
    if (ownerOrg) return ownerOrg.id
    return orgs[0]?.id || null
  }, [myFilesOrgId, orgs])

  // Keep a stable stored org id for "My Files"
  useEffect(() => {
    const resolved = (() => {
      if (myFilesOrgId) return myFilesOrgId
      const ntgOrg = orgs.find((o) => o.name?.toLowerCase() === 'ntg')
      if (ntgOrg) return ntgOrg.id
      const ownerOrg = orgs.find((o) => o.role === 'owner')
      if (ownerOrg) return ownerOrg.id
      return orgs[0]?.id || null
    })()

    if (resolved && resolved !== myFilesOrgId) {
      setMyFilesOrgId(resolved)
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('myFilesOrgId', resolved)
      }
    }
  }, [orgs, myFilesOrgId])

  const downloadFileInOrg = async (orgId: string, file: FileWithVersion) => {
    try {
      const blob = await api.files.downloadBlob(orgId, file.id)
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  // If My Files org isn't known, probe orgs for a user-owned root folder and lock onto the first that works
  useEffect(() => {
    if (resolvingMyFilesOrg) {
      setResolvingMyFilesOrg(false)
    }
  }, [resolvingMyFilesOrg, myFilesOrgId, user, orgs])

  // PDF viewer state
  const [viewingPdf, setViewingPdf] = useState<FileWithVersion | null>(null)
  const [viewingMarkdown, setViewingMarkdown] = useState<FileWithVersion | null>(null)

  // Image viewer (lightbox) state
  const [viewingImage, setViewingImage] = useState<FileWithVersion | null>(null)
  const [viewingImageUrl, setViewingImageUrl] = useState<string | null>(null)
  const [viewingImageLoading, setViewingImageLoading] = useState(false)
  const [viewingImageError, setViewingImageError] = useState<string | null>(null)

  const closeImageLightbox = useCallback(() => {
    setViewingImage(null)
    setViewingImageError(null)
    setViewingImageLoading(false)
    if (viewingImageUrl) {
      window.URL.revokeObjectURL(viewingImageUrl)
    }
    setViewingImageUrl(null)
  }, [viewingImageUrl])

  const openImageLightbox = useCallback(
    async (file: FileWithVersion, fallbackOrgId?: string | null) => {
      closeImageLightbox()
      setViewingImage(file)
      setViewingImageLoading(true)
      setViewingImageError(null)

      try {
        const orgId = file.org_id || fallbackOrgId
        if (!orgId) {
          throw new Error('No organization context')
        }
        const blob = await api.files.downloadBlob(orgId, file.id, { inline: true })
        const url = window.URL.createObjectURL(blob)
        setViewingImageUrl(url)
      } catch {
        setViewingImageError('Failed to load image')
      } finally {
        setViewingImageLoading(false)
      }
    },
    [closeImageLightbox]
  )

  // Share modal state
  const [sharingFile, setSharingFile] = useState<FileWithVersion | null>(null)
  const [sharingFolder, setSharingFolder] = useState<Folder | null>(null)

  // Move file state
  const [movingFile, setMovingFile] = useState<FileWithVersion | null>(null)
  const [pendingDeleteFile, setPendingDeleteFile] = useState<FileWithVersion | null>(null)

  const isMoveDialogOpen = movingFile != null
  const movingFileName = movingFile?.name

  const [moveScope, setMoveScope] = useState<'my-files' | 'org' | 'team'>(() => {
    if (activeDomain?.type === 'team') return 'team'
    if (activeDomain?.type === 'my-files') return 'my-files'
    return 'org'
  })
  const [moveTeamId, setMoveTeamId] = useState<string | null>(null)
  const [moveFolderId, setMoveFolderId] = useState<string | null>(null)
  const [moveFolderPath, setMoveFolderPath] = useState<Folder[]>([])
  const [moveChildFolders, setMoveChildFolders] = useState<Folder[]>([])
  const [moveLoading, setMoveLoading] = useState(false)

  const ALL_TAGS_VALUE = '__all__'

  const filePickerRef = useRef<HTMLInputElement | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const dragDepthRef = useRef(0)

  const folderId = searchParams.get('folder')
  const domainType = searchParams.get('domain')
  const domainId = searchParams.get('domainId')

  // A filter only makes sense for the folder it was typed in.
  useEffect(() => {
    setSearchQuery('')
  }, [folderId, domainType, domainId])

  // Track loading state to prevent duplicate concurrent fetches
  const teamsLoadedRef = useRef(false)
  const tagsLoadedForOrgRef = useRef<string | null>(null)
  const folderFetchInFlightRef = useRef<string | null>(null)
  const moveFolderFetchInFlightRef = useRef<string | null>(null)
  const moveFolderLastLoadedKeyRef = useRef<string | null>(null)

  // Load all teams across all orgs (only once)
  useEffect(() => {
    if (teamsLoadedRef.current || orgs.length === 0) return
    teamsLoadedRef.current = true
    
    const loadAllTeams = async () => {
      const teamsList: { team: Team; org: Org }[] = []
      for (const org of orgs) {
        try {
          const { teams: orgTeams } = await api.teams.my(org.id)
          for (const team of orgTeams || []) {
            teamsList.push({ team, org })
          }
        } catch {
          // Ignore errors for individual orgs
        }
      }
      setAllTeams(teamsList)
    }
    loadAllTeams()
  }, [orgs])

  const loadPublicSharedByMe = useCallback(async () => {
    if (!orgs || orgs.length === 0) return

    setPublicSharedLoading(true)
    try {
      const items: Array<PublicSharedFileItem & { orgId: string; orgName: string }> = []
      for (const o of orgs) {
        try {
          const res = await api.shares.listMyPublicSharedFiles(o.id)
          for (const it of res.items || []) {
            items.push({ ...it, orgId: o.id, orgName: o.name })
          }
        } catch {
          // Ignore inaccessible orgs
        }
      }
      setPublicSharedItems(items)
    } finally {
      setPublicSharedLoading(false)
    }
  }, [orgs])

  // Load folder contents when folderId changes - ALWAYS fetch to ensure correct data
  useEffect(() => {
    if (!folderId || !activeDomain) return
    
    // Determine which org to use based on active domain
    let orgId: string | null = null
    if (activeDomain.type === 'org') {
      orgId = activeDomain.org.id
    } else if (activeDomain.type === 'team') {
      orgId = activeDomain.org.id
    } else if (activeDomain.type === 'my-files') {
      orgId = getMyFilesOrgId()
    }
    
    if (!orgId) return
    
    // Prevent duplicate concurrent fetches for the same folder
    if (folderFetchInFlightRef.current === folderId) return
    folderFetchInFlightRef.current = folderId
    
    fetchFolderContents(orgId, folderId)
      .catch(() => {
        console.error('Failed to load folder')
      })
      .finally(() => {
        // Only clear if this is still the current fetch
        if (folderFetchInFlightRef.current === folderId) {
          folderFetchInFlightRef.current = null
        }
      })
  }, [folderId, activeDomain, orgs, fetchFolderContents, getMyFilesOrgId])

  // Force reload current folder (used after uploads, deletes, etc.)
  const loadFolder = useCallback(() => {
    if (!folderId || !activeDomain) return Promise.resolve()

    const orgId =
      activeDomain.type === 'org'
        ? activeDomain.org.id
        : activeDomain.type === 'team'
          ? activeDomain.org.id
          : activeDomain.type === 'my-files'
            ? getMyFilesOrgId()
            : null

    if (folderId && orgId) {
      setIsFavoritesView(false)
      folderFetchInFlightRef.current = null // Allow new fetch
      return fetchFolderContents(orgId, folderId)
        .catch(() => {
          console.error('Failed to load folder')
        })
    }
    return Promise.resolve()
  }, [folderId, fetchFolderContents, activeDomain, getMyFilesOrgId])

  const handleRefreshFolder = useCallback(() => {
    if (isRefreshing) return
    const maybePromise = loadFolder()
    if (!maybePromise || typeof maybePromise.finally !== 'function') {
      return
    }
    setIsRefreshing(true)
    maybePromise.finally(() => setIsRefreshing(false))
  }, [isRefreshing, loadFolder])

  // Load available tags for the current org (only when org changes)
  useEffect(() => {
    const orgId = activeDomain?.type === 'org' ? activeDomain.org.id
      : activeDomain?.type === 'team' ? activeDomain.org.id
      : activeDomain?.type === 'my-files' ? getMyFilesOrgId()
      : null
    
    if (!orgId || tagsLoadedForOrgRef.current === orgId) return
    tagsLoadedForOrgRef.current = orgId
    
    api.tags.list(orgId).then(({ tags }) => {
      setAvailableTags(tags || [])
    }).catch(() => {
      setAvailableTags([])
    })
  }, [activeDomain, getMyFilesOrgId, orgs])

	useEffect(() => {
		if (typeof window === 'undefined') return
		window.localStorage.setItem('filesViewMode', viewMode)
	}, [viewMode])

  const handleDomainClick = useCallback(async (domain: SecurityDomain) => {

    let orgId: string | null = null
    let ownerType: string
    let ownerId: string
    const resolvedDomain: SecurityDomain | null = domain

    if (domain.type === 'my-files') {
      if (!user) return
      orgId = getMyFilesOrgId()
      if (!orgId) {
        toast({ title: 'My Files unavailable', variant: 'destructive' })
        return
      }
      ownerType = 'user'
      ownerId = user.id
      const org = orgs.find((o) => o.id === orgId)
      if (org) setCurrentOrg(org)
    } else if (domain.type === 'org') {
      orgId = domain.org.id
      ownerType = 'org'
      ownerId = domain.org.id
      setCurrentOrg(domain.org)
    } else if (domain.type === 'team') {
      orgId = domain.org.id
      ownerType = 'team'
      ownerId = domain.team.id
      setCurrentOrg(domain.org)
    } else {
      // Shared with Me domain doesn't map to a single folder tree. We render a custom view.
      setActiveDomain(resolvedDomain)
      setSearchParams({ domain: resolvedDomain.type === 'public-shared' ? 'public-shared' : 'shared' })
      return
    }

    try {
      const { folder } = await api.folders.root(orgId, ownerType, ownerId)
      setActiveDomain(resolvedDomain)
      setSearchParams({
        domain: resolvedDomain.type === 'my-files' ? 'my-files' : resolvedDomain.type,
        domainId: resolvedDomain.type === 'org' ? resolvedDomain.org.id : resolvedDomain.type === 'team' ? resolvedDomain.team.id : '',
        folder: folder.id,
      })
      return
    } catch (e) {
      toast({ title: 'Failed to load folder, falling back to org', variant: 'destructive' })
    }

    // Fallback to first org if My Files fails
    const fallbackOrg = orgs[0]
    if (!fallbackOrg) return
    try {
      const { folder } = await api.folders.root(fallbackOrg.id, 'org', fallbackOrg.id)
      setCurrentOrg(fallbackOrg)
      setActiveDomain({ type: 'org', org: fallbackOrg })
      setSearchParams({ domain: 'org', domainId: fallbackOrg.id, folder: folder.id })
    } catch {
      toast({ title: 'Failed to load any folder', variant: 'destructive' })
    }
  }, [getMyFilesOrgId, orgs, setCurrentOrg, setSearchParams, user])

  const loadSharedWithMe = useCallback(async () => {
    if (!orgs || orgs.length === 0) return

    setSharedLoading(true)
    try {
      const items: Array<SharedItem & { orgId: string; orgName: string }> = []
      const dropped: Array<DroppedItem & { orgId: string; orgName: string }> = []
      for (const o of orgs) {
        try {
          const res = await api.shares.listSharedWithMe(o.id)
          for (const it of res.items || []) {
            items.push({ ...it, orgId: o.id, orgName: o.name })
          }
          for (const it of res.dropped || []) {
            dropped.push({ ...it, orgId: o.id, orgName: o.name })
          }
        } catch {
          // Ignore orgs where the user has no access / session mismatch
        }
      }
      setSharedItems(items)
      setDroppedItems(dropped)
    } finally {
      setSharedLoading(false)
    }
  }, [orgs])

  useEffect(() => {
    if (activeDomain?.type === 'shared') {
      loadSharedWithMe()
    }
    if (activeDomain?.type === 'public-shared') {
      loadPublicSharedByMe()
    }
  }, [activeDomain, loadSharedWithMe, loadPublicSharedByMe])

  // Determine active scope from URL params
  useEffect(() => {
    const matchesCurrentDomain = () => {
      if (!activeDomain) return false
      if (domainType === 'my-files') return activeDomain.type === 'my-files'
      if (domainType === 'org' && domainId) return activeDomain.type === 'org' && activeDomain.org.id === domainId
      if (domainType === 'team' && domainId) return activeDomain.type === 'team' && activeDomain.team.id === domainId
      if (domainType === 'shared') return activeDomain.type === 'shared'
      if (domainType === 'public-shared') return activeDomain.type === 'public-shared'
      return false
    }

    // Critical: do NOT rerun handleDomainClick when only folderId changes.
    // That would reset the URL back to the domain root folder and break navigation.
    if (matchesCurrentDomain()) {
      return
    }

    if (domainType === 'my-files') {
      handleDomainClick({ type: 'my-files' })
      return
    }
    if (domainType === 'org' && domainId) {
      const org = orgs.find((o) => o.id === domainId)
      if (org) {
        handleDomainClick({ type: 'org', org })
      }
      return
    }
    if (domainType === 'team' && domainId) {
      const found = allTeams.find((t) => t.team.id === domainId)
      if (found) {
        handleDomainClick({ type: 'team', team: found.team, org: found.org })
      }
      return
    }
    if (domainType === 'shared') {
      setActiveDomain((prev) => (prev?.type === 'shared' ? prev : { type: 'shared' }))
      return
    }
    if (domainType === 'public-shared') {
      setActiveDomain((prev) => (prev?.type === 'public-shared' ? prev : { type: 'public-shared' }))
      return
    }
    if (!folderId) {
      setActiveDomain(null)
    }
  }, [domainType, domainId, allTeams, folderId, orgs, handleDomainClick, activeDomain])

  const handleFolderClick = (folder: Folder) => {
    setIsFavoritesView(false)
    const params: Record<string, string> = { folder: folder.id }
    if (domainType) params.domain = domainType
    if (domainId) params.domainId = domainId
    setSearchParams(params)
  }

  const handleBreadcrumbClick = (folder: Folder) => {
    setIsFavoritesView(false)
    const params: Record<string, string> = { folder: folder.id }
    if (domainType) params.domain = domainType
    if (domainId) params.domainId = domainId
    setSearchParams(params)
  }

  const handleBackToDomains = () => {
    setActiveDomain(null)
    setSearchParams({})
  }

  const getCurrentOrgId = useCallback((): string | null => {
    if (activeDomain?.type === 'org') return activeDomain.org.id
    if (activeDomain?.type === 'team') return activeDomain.org.id
    if (activeDomain?.type === 'my-files') return getMyFilesOrgId()
    return null
  }, [activeDomain, getMyFilesOrgId])

	const loadFavorites = useCallback(async () => {
		const orgId = getCurrentOrgId()
		if (!orgId) {
			setFavoriteIds(new Set())
			setFavoritesFiles([])
			return
		}
		try {
			const res = await api.favorites.list(orgId)
			setFavoritesFiles(res.files || [])
			setFavoriteIds(new Set((res.files || []).map((f) => f.id)))
		} catch {
			setFavoritesFiles([])
			setFavoriteIds(new Set())
		}
	}, [getCurrentOrgId])

	useEffect(() => {
		setIsFavoritesView(false)
		loadFavorites()
	}, [activeDomain, loadFavorites])

  const getMoveOrgId = useCallback((): string | null => {
    if (moveScope === 'my-files') return getMyFilesOrgId()
    return getCurrentOrgId()
  }, [getCurrentOrgId, getMyFilesOrgId, moveScope])

  const resolveMoveRoot = useCallback(async () => {
    if (!user) return
    const orgId = getMoveOrgId()
    if (!orgId) return

    setMoveLoading(true)
    try {
      let ownerType: string
      let ownerId: string

      if (moveScope === 'my-files') {
        ownerType = 'user'
        ownerId = user.id
      } else if (moveScope === 'team') {
        if (!moveTeamId) {
          const defaultTeam = allTeams.find((t) => t.org.id === orgId)
          if (defaultTeam) {
            setMoveTeamId(defaultTeam.team.id)
            return
          }
          ownerType = 'org'
          ownerId = orgId
        } else {
          ownerType = 'team'
          ownerId = moveTeamId
        }
      } else {
        ownerType = 'org'
        ownerId = orgId
      }

      const { folder } = await api.folders.root(orgId, ownerType, ownerId)
      setMoveFolderId(folder.id)
    } catch {
      toast({ title: 'Failed to load destination root folder', variant: 'destructive' })
    } finally {
      setMoveLoading(false)
    }
  }, [allTeams, getMoveOrgId, moveScope, moveTeamId, user])

  const loadMoveFolder = useCallback(
    async (folderId: string) => {
      const orgId = getMoveOrgId()
      if (!orgId) return

      const key = `${orgId}:${folderId}`
      if (moveFolderFetchInFlightRef.current === key) return
      if (moveFolderLastLoadedKeyRef.current === key) return

      moveFolderFetchInFlightRef.current = key

      setMoveLoading(true)
      try {
        const { folders, path } = await api.folders.contents(orgId, folderId)
        setMoveFolderId(folderId)
        setMoveChildFolders((folders || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')))
        setMoveFolderPath(path || [])
        moveFolderLastLoadedKeyRef.current = key
      } catch {
        toast({ title: 'Failed to load destination folder', variant: 'destructive' })
      } finally {
        setMoveLoading(false)
        if (moveFolderFetchInFlightRef.current === key) {
          moveFolderFetchInFlightRef.current = null
        }
      }
    },
    [getMoveOrgId]
  )

  useEffect(() => {
    if (!isMoveDialogOpen) return

    if (activeDomain?.type === 'team') {
      setMoveScope('team')
      setMoveTeamId(activeDomain.team.id)
    } else if (activeDomain?.type === 'my-files') {
      setMoveScope('my-files')
      setMoveTeamId(null)
    } else {
      setMoveScope('org')
      setMoveTeamId(null)
    }
  }, [activeDomain, isMoveDialogOpen])

  useEffect(() => {
    if (!isMoveDialogOpen) return
    setMoveFolderId(null)
    setMoveFolderPath([])
    setMoveChildFolders([])
    moveFolderFetchInFlightRef.current = null
    moveFolderLastLoadedKeyRef.current = null
    resolveMoveRoot()
  }, [isMoveDialogOpen, moveScope, moveTeamId, resolveMoveRoot])

  useEffect(() => {
    if (!isMoveDialogOpen) return
    if (!moveFolderId) return
    loadMoveFolder(moveFolderId)
  }, [isMoveDialogOpen, loadMoveFolder, moveFolderId])

  const handleRenameFolder = async (folder: Folder) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    const name = prompt('Enter new folder name:', folder.name)
    if (!name || name === folder.name) return

    try {
      await api.folders.update(orgId, folder.id, { name })
      toast({ title: 'Folder renamed' })
      loadFolder()
    } catch {
      toast({ title: 'Failed to rename folder', variant: 'destructive' })
    }
  }

  const handleDeleteFolder = async (folder: Folder) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    try {
      const { folders, files } = await api.folders.contents(orgId, folder.id)
      const isEmpty = (folders?.length || 0) === 0 && (files?.length || 0) === 0
      if (!isEmpty) {
        toast({ title: 'Folder must be empty to delete', variant: 'destructive' })
        return
      }

      const ok = confirm(`Delete folder "${folder.name}"?`)
      if (!ok) return

      await api.folders.delete(orgId, folder.id)
      toast({ title: 'Folder deleted' })
      loadFolder()
    } catch {
      toast({ title: 'Failed to delete folder', variant: 'destructive' })
    }
  }

  const handleDownload = async (file: FileWithVersion) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    try {
      const blob = await api.files.downloadBlob(orgId, file.id)
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = file.name
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  const handleDownloadCompressed = async (file: FileWithVersion) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    try {
      const blob = await api.files.downloadBlob(orgId, file.id, { compressed: true })
      const blobUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = `${file.name}.gz`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  const handleDelete = async (file: FileWithVersion) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    try {
      await api.files.delete(orgId, file.id)
      toast({ title: 'File deleted' })
      loadFolder()
    } catch {
      toast({ title: 'Delete failed', variant: 'destructive' })
    }
  }

  const handleRename = async (file: FileWithVersion) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    const nextName = prompt('Enter new file name:', file.name)
    if (!nextName || !nextName.trim() || nextName.trim() === file.name) return

    try {
      await api.files.update(orgId, file.id, { name: nextName.trim() })
      toast({ title: 'File renamed' })

      loadFolder()
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Rename failed', variant: 'destructive' })
    }
  }

  const handleAddTag = async (file: FileWithVersion, tagId: string) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    try {
      await api.files.tags.add(orgId, file.id, { tag_id: tagId })
      loadFolder()
      toast({ title: 'Tag added' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to add tag', variant: 'destructive' })
    }
  }

  const handleRemoveTag = async (file: FileWithVersion, tagId: string) => {
    const orgId = getCurrentOrgId()
    if (!orgId) return

    try {
      await api.files.tags.remove(orgId, file.id, tagId)
      loadFolder()
      toast({ title: 'Tag removed' })
    } catch (e) {
      toast({ title: (e as Error)?.message || 'Failed to remove tag', variant: 'destructive' })
    }
  }

  const uploadFilesToCurrentFolder = useCallback(
    async (files: File[]) => {
      const orgId = getCurrentOrgId()
      if (!orgId || !currentFolder || files.length === 0) return

      try {
        setIsUploading(true)
        setUploadProgress(0)

        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          await api.files.upload.presigned(
            orgId,
            {
              folder_id: currentFolder.id,
              file,
              filename: file.name,
            },
            (p: number) => {
              const base = (i / files.length) * 100
              const next = base + p / files.length
              setUploadProgress(Math.floor(next))
            },
          )
        }

        toast({ title: files.length === 1 ? 'File uploaded successfully' : 'Files uploaded successfully' })
        loadFolder()
      } catch (e) {
        console.error('[FilesPage] Upload failed:', e)
        toast({ title: (e as Error)?.message || 'Upload failed', variant: 'destructive' })
      } finally {
        setIsUploading(false)
      }
    },
    [currentFolder, getCurrentOrgId, loadFolder],
  )

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return
    const files = Array.from(e.target.files)
    await uploadFilesToCurrentFolder(files)
    e.target.value = ''
  }

  const handleDropUpload = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepthRef.current = 0
    setIsDragOver(false)

    const items = Array.from(e.dataTransfer.items || [])
    const hasEntries = items.some((it: any) => typeof (it as any).webkitGetAsEntry === 'function')

    // Best-effort folder support (Chrome/Edge)
    if (hasEntries) {
      const getFilesFromEntry = async (entry: any, prefix: string): Promise<File[]> => {
        if (!entry) return []
        if (entry.isFile) {
          const file: File = await new Promise((resolve, reject) => {
            entry.file(resolve, reject)
          })
          // Preserve relative path in name for now; true folder creation would require creating folders server-side.
          const renamed = new globalThis.File([file], prefix ? `${prefix}/${file.name}` : file.name, { type: file.type })
          return [renamed]
        }
        if (entry.isDirectory) {
          const reader = entry.createReader()
          const entries: any[] = await new Promise((resolve, reject) => {
            reader.readEntries(resolve, reject)
          })
          const results: File[] = []
          for (const child of entries) {
            results.push(...(await getFilesFromEntry(child, prefix ? `${prefix}/${entry.name}` : entry.name)))
          }
          return results
        }
        return []
      }

      try {
        const files: File[] = []
        for (const it of items as any[]) {
          const entry = it.webkitGetAsEntry?.()
          if (entry) {
            files.push(...(await getFilesFromEntry(entry, '')))
          }
        }
        if (files.length > 0) {
          // If folders were dropped, we currently flatten into filenames with path prefixes.
          // Proper folder creation can be added next.
          await uploadFilesToCurrentFolder(files)
          return
        }
      } catch {
        // Fall back to file list
      }
    }

    const droppedFiles = Array.from(e.dataTransfer.files || [])
    if (droppedFiles.length === 0) {
      toast({ title: 'Nothing to upload', variant: 'destructive' })
      return
    }

    await uploadFilesToCurrentFolder(droppedFiles)
  }

  const handleCreateFolder = async () => {
    const orgId = getCurrentOrgId()
    if (!orgId) {
      toast({ title: 'Please select a location first', variant: 'destructive' })
      return
    }

    const parentId = currentFolder?.id || folderId
    if (!parentId) {
      toast({ title: 'No folder selected', variant: 'destructive' })
      return
    }

    const name = prompt('Enter folder name:')
    if (!name) return

    try {
      await api.folders.create(orgId, {
        parent_id: parentId,
        name,
      })
      toast({ title: 'Folder created' })
      loadFolder()
    } catch {
      toast({ title: 'Failed to create folder', variant: 'destructive' })
    }
  }

  const displayFiles = folderContents.files
	const displayFilesWithFavorites = isFavoritesView ? favoritesFiles : displayFiles

	const sortedFiles = useMemo(() => {
		let files = [...displayFilesWithFavorites]

		// Apply tag filter
		if (filterTagId) {
			files = files.filter((f) => f.tags?.some((t) => t.id === filterTagId))
		}

		// Apply live text filter: every term must match some piece of metadata
		if (filterTerms.length > 0) {
			files = files.filter((f) => {
				const haystack = [
					f.name,
					f.description,
					f.current_version?.mime_type,
					...(f.tags || []).map((t) => t.name),
				]
					.filter(Boolean)
					.join('\n')
					.toLowerCase()
				return filterTerms.every((term) => haystack.includes(term))
			})
		}

		const dir = sortDir === 'asc' ? 1 : -1

		files.sort((a, b) => {
			if (sortKey === 'name') {
				return a.name.localeCompare(b.name) * dir
			}

			if (sortKey === 'size') {
				const aSize = a.current_version?.size ?? 0
				const bSize = b.current_version?.size ?? 0
				return (aSize - bSize) * dir
			}

			if (sortKey === 'created_at') {
				const aTime = new Date(a.created_at).getTime()
				const bTime = new Date(b.created_at).getTime()
				return (aTime - bTime) * dir
			}

			const aTime = new Date(a.updated_at).getTime()
			const bTime = new Date(b.updated_at).getTime()
			return (aTime - bTime) * dir
		})

		return files
	}, [displayFilesWithFavorites, sortDir, sortKey, filterTagId, filterTerms])

	const toggleFavorite = useCallback(
		async (file: FileWithVersion) => {
			const orgId = getCurrentOrgId()
			if (!orgId) return

			const isFav = favoriteIds.has(file.id)
			setFavoriteIds((prev) => {
				const next = new Set(prev)
				if (isFav) next.delete(file.id)
				else next.add(file.id)
				return next
			})

			try {
				if (isFav) {
					await api.files.favorite.remove(orgId, file.id)
				} else {
					await api.files.favorite.add(orgId, file.id)
				}
			} catch (e) {
				setFavoriteIds((prev) => {
					const next = new Set(prev)
					if (isFav) next.add(file.id)
					else next.delete(file.id)
					return next
				})
				toast({ title: (e as Error)?.message || 'Failed to update favorite', variant: 'destructive' })
			}

			loadFavorites()
		},
		[favoriteIds, getCurrentOrgId, loadFavorites],
	)

	type ItemRow =
		| { kind: 'folder'; folder: Folder }
		| { kind: 'file'; file: FileWithVersion }

	const visibleFolders = useMemo(() => {
		if (filterTerms.length === 0) return folderContents.folders
		return folderContents.folders.filter((folder) => {
			const name = folder.name.toLowerCase()
			return filterTerms.every((term) => name.includes(term))
		})
	}, [folderContents.folders, filterTerms])

	const minimalRows: ItemRow[] = useMemo(() => {
		const rows: ItemRow[] = []
		for (const folder of visibleFolders) rows.push({ kind: 'folder', folder })
		for (const file of sortedFiles) rows.push({ kind: 'file', file })
		return rows
	}, [visibleFolders, sortedFiles])

	const visibleOrgs = useMemo(() => {
		if (!user) return orgs
		const personalOrgSlug = `user-${String(user.id).toLowerCase()}`
		return orgs.filter((org) => String(org.slug || '').toLowerCase() !== personalOrgSlug)
	}, [orgs, user])

  // Build domain folders list
  const domainFolders: DomainFolder[] = [
    {
      id: 'my-files',
      name: 'My Files',
      icon: User,
      iconColor: 'text-green-500',
      description: 'Your personal files',
      domain: { type: 'my-files' },
    },
    ...visibleOrgs.map(org => ({
      id: `org-${org.id}`,
      name: org.name,
      icon: Building2,
      iconColor: 'text-blue-500',
      description: 'Organization files',
      domain: { type: 'org' as const, org },
    })),
    ...allTeams.map(({ team, org }) => ({
      id: `team-${team.id}`,
      name: team.name,
      icon: Users,
      iconColor: 'text-purple-500',
      description: `Team in ${org.name}`,
      domain: { type: 'team' as const, team, org },
    })),
    {
      id: 'shared',
      name: 'Shared with Me',
      icon: Share,
      iconColor: 'text-orange-500',
      description: 'Files others have shared with you',
      domain: { type: 'shared' },
    },
    {
      id: 'public-shared',
      name: 'Files I Share Publicly',
      icon: Share2,
      iconColor: 'text-rose-500',
      description: 'Audit public file links and access',
      domain: { type: 'public-shared' },
    },
  ]

  // Show domain picker if no domain is active
  if (!activeDomain && !folderId) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Files</h1>
          <p className="text-muted-foreground">Choose where to browse files</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {domainFolders.map((df) => (
            <button
              key={df.id}
              onClick={() => handleDomainClick(df.domain)}
              className="flex flex-col items-center p-6 rounded-lg border bg-card hover:bg-accent transition-colors text-center"
            >
              <df.icon className={`h-12 w-12 ${df.iconColor} mb-3`} />
              <span className="font-medium">{df.name}</span>
              <span className="text-xs text-muted-foreground mt-1">{df.description}</span>
            </button>
          ))}
        </div>

        {domainFolders.length === 2 && (
          <div className="mt-8 text-center text-muted-foreground">
            <p>You're not a member of any organizations or teams yet.</p>
            <p className="text-sm mt-1">
              Go to <span className="font-medium">Settings</span> to create or join an organization.
            </p>
          </div>
        )}
      </div>
    )
  }

  // Get current domain name for breadcrumb
  const getDomainName = () => {
    if (activeDomain?.type === 'my-files') return 'My Files'
    if (activeDomain?.type === 'org') return activeDomain.org.name
    if (activeDomain?.type === 'team') return activeDomain.team.name
    if (activeDomain?.type === 'shared') return 'Shared with Me'
    if (activeDomain?.type === 'public-shared') return 'Files I Share Publicly'
    return 'Files'
  }

  const domainName = getDomainName()
  const path = folderContents.path || []
  const domainRootFolder = path.length > 0 ? path[0] : null
  const breadcrumbPath = path.length > 0 ? path.slice(1) : path

  if (activeDomain?.type === 'shared') {
    const sharedGroups = new Map<
      string,
      { orgName: string; items: typeof sharedItems; dropped: typeof droppedItems }
    >()
    const groupFor = (orgId: string, orgName: string) => {
      let group = sharedGroups.get(orgId)
      if (!group) {
        group = { orgName, items: [], dropped: [] }
        sharedGroups.set(orgId, group)
      }
      return group
    }
    for (const it of sharedItems) groupFor(it.orgId, it.orgName).items.push(it)
    for (const it of droppedItems) groupFor(it.orgId, it.orgName).dropped.push(it)

    const renderSharedFileCard = (key: string, file: NonNullable<SharedItem['file']>, orgId: string, subtitle: string) => (
      <div key={key} className="p-4 rounded-lg border bg-card">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium truncate">{file.name}</div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const { file: full } = await api.files.get(orgId, file.id)
                    const mime = (full as any)?.current_version?.mime_type
                    if (mime === 'application/pdf') {
                      setViewingPdf(full as FileWithVersion)
                    } else if (isMarkdownFile(full as FileWithVersion)) {
                      setViewingMarkdown(full as FileWithVersion)
                    } else {
                      await downloadFileInOrg(orgId, full as FileWithVersion)
                    }
                  } catch {
                    toast({ title: 'Failed to open file', variant: 'destructive' })
                  }
                }}
              >
                <File className="h-4 w-4 mr-2" />
                Open
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    const { file: full } = await api.files.get(orgId, file.id)
                    await downloadFileInOrg(orgId, full as FileWithVersion)
                  } catch {
                    toast({ title: 'Download failed', variant: 'destructive' })
                  }
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    )

    return (
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <button
              onClick={handleBackToDomains}
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Home className="h-3 w-3" />
              Files
            </button>
            <ChevronRight className="h-4 w-4 mx-1" />
            <span className="text-foreground font-medium">Shared with Me</span>
          </div>
        </div>

        {sharedLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : sharedItems.length === 0 && droppedItems.length === 0 ? (
          <div className="text-muted-foreground">Nothing has been shared with you yet.</div>
        ) : (
          <div className="space-y-4">
            {Array.from(sharedGroups.entries()).map(([orgId, { orgName, items, dropped }]) => (
              <div key={orgId} className="space-y-2">
                <div className="text-sm font-medium text-muted-foreground">{orgName}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {items.map((it) => {
                    if (it.file) {
                      return renderSharedFileCard(it.share.id, it.file, orgId, `Permission: ${it.share.permission}`)
                    }

                    if (it.folder) {
                      return (
                        <button
                          key={it.share.id}
                          type="button"
                          className="p-4 rounded-lg border bg-card text-left hover:bg-accent transition-colors"
                          onClick={() => {
                            const org = orgs.find((o) => o.id === orgId)
                            if (!org) {
                              toast({ title: 'Org not found for shared item', variant: 'destructive' })
                              return
                            }
                            setCurrentOrg(org)
                            setActiveDomain({ type: 'org', org })
                            setSearchParams({ domain: 'org', domainId: org.id, folder: it.folder!.id })
                          }}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="font-medium truncate">{it.folder.name}</div>
                              <div className="text-xs text-muted-foreground">Folder • Permission: {it.share.permission}</div>
                            </div>
                            <FolderOpen className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </button>
                      )
                    }

                    return null
                  })}
                  {dropped.map((d) =>
                    renderSharedFileCard(
                      `drop-${d.file.id}`,
                      d.file,
                      orgId,
                      `${d.label ? `Dropped via "${d.label}"` : 'Dropped via public link'} • ${new Date(d.received_at).toLocaleDateString()}`
                    )
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PDF Viewer Sheet */}
        <Sheet open={!!viewingPdf} onOpenChange={(open) => !open && setViewingPdf(null)}>
          <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>{viewingPdf?.name || 'PDF Viewer'}</SheetTitle>
            </SheetHeader>
            {viewingPdf && (
              <PDFViewer
                orgId={viewingPdf.org_id}
                fileId={viewingPdf.id}
                fileName={viewingPdf.name}
                onDownload={() => handleDownload(viewingPdf)}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Markdown Viewer Sheet */}
        <Sheet open={!!viewingMarkdown} onOpenChange={(open) => !open && setViewingMarkdown(null)}>
          <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>{viewingMarkdown?.name || 'Markdown Viewer'}</SheetTitle>
            </SheetHeader>
            {viewingMarkdown && (
              <MarkdownViewer
                orgId={viewingMarkdown.org_id}
                fileId={viewingMarkdown.id}
                fileName={viewingMarkdown.name}
                onDownload={() => handleDownload(viewingMarkdown)}
              />
            )}
          </SheetContent>
        </Sheet>

        {/* Image Lightbox */}
        <Dialog
          open={!!viewingImage}
          onOpenChange={(open) => {
            if (!open) closeImageLightbox()
          }}
        >
          <DialogContent className="max-w-none w-[100vw] h-[100vh] p-0 bg-black border-none">
            <div className="absolute top-3 right-3 z-10">
              <Button variant="secondary" size="icon" onClick={closeImageLightbox}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="w-full h-full flex items-center justify-center">
              {viewingImageLoading ? (
                <div className="flex items-center gap-2 text-white">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : viewingImageError ? (
                <div className="text-white">{viewingImageError}</div>
              ) : viewingImageUrl ? (
                <img
                  src={viewingImageUrl}
                  alt={viewingImage?.name || 'image'}
                  className="max-w-full max-h-full object-contain"
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  if (activeDomain?.type === 'public-shared') {
    return (
      <div className="p-6">
        <div className="mb-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <button
              onClick={handleBackToDomains}
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Home className="h-3 w-3" />
              Files
            </button>
            <ChevronRight className="h-4 w-4 mx-1" />
            <span className="text-foreground font-medium">Files I Share Publicly</span>
          </div>

          <Button
            variant="outline"
            onClick={async () => {
              const logs: PublicFileAccessLog[] = []
              for (const o of orgs) {
                try {
                  const res = await api.shares.listMyPublicAccessLogs(o.id)
                  logs.push(...(res.logs || []))
                } catch {
                  // Ignore org access errors
                }
              }
              setPublicAccessLogs(logs.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()))
              setPublicAccessLogsTitle('All Public Share Access')
              setPublicAccessLogsOpen(true)
            }}
          >
            View All Access Logs
          </Button>
        </div>

        {publicSharedLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        ) : publicSharedItems.length === 0 ? (
          <div className="text-muted-foreground">No files are publicly shared right now.</div>
        ) : (
          <div className="space-y-4">
            {publicSharedItems.map((item) => (
              <div key={`${item.orgId}-${item.file_id}`} className="p-4 rounded-lg border bg-card">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{item.file?.name || item.file_id}</div>
                    <div className="text-xs text-muted-foreground">
                      {item.file?.owner_type === 'user' ? 'My Files' : item.orgName} • {item.enabled_count} enabled link(s) • {item.links.length} total link(s)
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const res = await api.shares.listPublicAccessLogsForFile(item.orgId, item.file_id)
                        setPublicAccessLogs(res.logs || [])
                        setPublicAccessLogsTitle(`Access: ${item.file?.name || item.file_id}`)
                        setPublicAccessLogsOpen(true)
                      } catch {
                        toast({ title: 'Failed to load access logs', variant: 'destructive' })
                      }
                    }}
                  >
                    File Access Logs
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={publicAccessLogsOpen} onOpenChange={setPublicAccessLogsOpen}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle>{publicAccessLogsTitle}</DialogTitle>
              <DialogDescription>Most recent public-link access events</DialogDescription>
            </DialogHeader>
            <div className="max-h-[360px] overflow-auto space-y-2 text-sm">
              {publicAccessLogs.length === 0 ? (
                <p className="text-muted-foreground">No access events recorded.</p>
              ) : (
                publicAccessLogs.map((log) => (
                  <div key={log.id} className="rounded border p-2.5">
                    <div className="font-medium">{new Date(log.created_at).toLocaleString()}</div>
                    <div className="text-xs text-muted-foreground">IP: {log.ip_address || 'Unknown'} • Link: {log.link_id}</div>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  return (
    <div className="p-6">
      <input
        ref={filePickerRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleUpload}
      />
      {/* Stays pinned while the file list scrolls (below the mobile bar on tablets, at the top on desktop). */}
      <div className="z-10 -mx-6 -mt-6 mb-4 flex items-center justify-between border-b bg-background/95 px-6 pb-3 pt-6 backdrop-blur supports-[backdrop-filter]:bg-background/85 md:sticky md:top-14 lg:top-0">
        <div>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <button
              onClick={handleBackToDomains}
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Home className="h-3 w-3" />
              Files
            </button>
            <ChevronRight className="h-4 w-4 mx-1" />
            {domainRootFolder ? (
              <button
                onClick={() => handleBreadcrumbClick(domainRootFolder)}
                className="text-foreground font-medium hover:text-foreground/80 transition-colors"
              >
                {domainName}
              </button>
            ) : (
              <span className="text-foreground font-medium">{domainName}</span>
            )}
            {isFavoritesView && (
              <>
                <ChevronRight className="h-4 w-4 mx-1" />
                <span className="text-foreground font-medium">Favorites</span>
              </>
            )}
            {breadcrumbPath.length > 0 && breadcrumbPath.map((folder, idx) => {
              const isLast = idx === breadcrumbPath.length - 1
              const label = isLast ? `${folder.name} (${folderContents.files.length})` : folder.name
              return (
                <span key={folder.id} className="flex items-center">
                  <ChevronRight className="h-4 w-4 mx-1" />
                  <button
                    onClick={() => handleBreadcrumbClick(folder)}
                    className="hover:text-foreground transition-colors"
                  >
                    {label}
                  </button>
                </span>
              )
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter this folder..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Escape' && setSearchQuery('')}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            {availableTags.length > 0 && (
              <div className="w-12">
                <Select
                  value={filterTagId ?? ALL_TAGS_VALUE}
                  onValueChange={(v) => setFilterTagId(v === ALL_TAGS_VALUE ? null : v)}
                >
                  <SelectTrigger className="px-2" aria-label="Tag filter">
                    <TagIcon className="h-4 w-4" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_TAGS_VALUE}>All files</SelectItem>
                    {availableTags.map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color || '#6b7280' }} />
                          {tag.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="w-28">
              <Select value={viewMode} onValueChange={(v) => setViewMode(v as any)}>
                <SelectTrigger className="px-2">
                  <span>View</span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="trading">Trading Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            >
              <ArrowUpDown className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefreshFolder}
              disabled={isRefreshing}
              title="Refresh folder contents"
            >
              {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="icon" onClick={handleCreateFolder} title="New Folder">
              <FolderPlus className="h-4 w-4" />
            </Button>
            <Button asChild disabled={isUploading}>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload
                <input type="file" multiple className="hidden" onChange={handleUpload} />
              </label>
            </Button>
          </div>
        </div>
      </div>

      {isUploading && (
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>Uploading...</span>
          <span>{uploadProgress}%</span>
        </div>
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${uploadProgress}%` }}
          />
        </div>
      </div>
    )}

    <div
      onDragEnter={(e) => {
        e.preventDefault()
        e.stopPropagation()
        dragDepthRef.current += 1
        setIsDragOver(true)
      }}
      onDragOver={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragOver(true)
      }}
      onDragLeave={(e) => {
        e.preventDefault()
        e.stopPropagation()
        dragDepthRef.current -= 1
        if (dragDepthRef.current <= 0) {
          dragDepthRef.current = 0
          setIsDragOver(false)
        }
      }}
      onDrop={handleDropUpload}
      className="relative min-h-[60vh]"
    >
      {isDragOver && currentFolder && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm border-2 border-dashed border-primary rounded-lg">
          <div className="text-center">
            <Upload className="h-10 w-10 mx-auto mb-2 text-primary" />
            <p className="font-medium">Drop files to upload</p>
            <p className="text-sm text-muted-foreground">They’ll be uploaded to {currentFolder.name}</p>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-4">
          {viewMode !== 'minimal' && visibleFolders.length > 0 && (
            <div>
              <h2 className="text-sm font-medium text-muted-foreground mb-2">Folders</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {favoriteIds.size > 0 && (
                  <div className="group relative rounded-xl border bg-card p-4 transition-all hover:border-primary/30 hover:bg-accent/60 hover:shadow-sm">
                    <button
                      onClick={() => {
                        setIsFavoritesView(true)
                        loadFavorites()
                      }}
                      className="flex flex-col items-center w-full text-center"
                    >
                      <Star className="mb-2 h-10 w-10 text-amber-500" />
                      <span className="text-sm font-medium truncate w-full">Favorites</span>
                      <span className="text-xs text-muted-foreground mt-1">{favoriteIds.size}</span>
                    </button>
                  </div>
                )}
                {visibleFolders.map((folder) => (
                  <div
                    key={folder.id}
                    className="group relative rounded-xl border bg-card p-4 transition-all hover:border-primary/30 hover:bg-accent/60 hover:shadow-sm"
                  >
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute top-1 right-1 h-8 w-8"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            setSharingFolder(folder)
                          }}
                        >
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRenameFolder(folder)
                          }}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Rename
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDeleteFolder(folder)
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <button
                      onClick={() => handleFolderClick(folder)}
                      className="flex flex-col items-center w-full text-center"
                    >
                      <FolderOpen className="mb-2 h-10 w-10 text-amber-500" />
                      <span className="text-sm font-medium truncate w-full">{folder.name}</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            {isFiltering && (
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-medium text-muted-foreground">
                  {sortedFiles.length + visibleFolders.length} matching
                </h2>
                <Button variant="ghost" size="sm" onClick={() => setSearchQuery('')}>
                  Clear search
                </Button>
              </div>
            )}
            {viewMode === 'minimal' ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground border-b">
                      <th className="text-left font-medium py-2 pr-4">Name</th>
                      <th className="text-right font-medium py-2 pr-4">Size</th>
                      <th className="text-left font-medium py-2 pr-4">Date</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody>
                    {minimalRows.map((row) => {
                      if (row.kind === 'folder') {
                        return (
                          <tr key={`folder-${row.folder.id}`} className="border-b hover:bg-muted/50">
                            <td className="py-2 pr-4">
                              <button
                                onClick={() => handleFolderClick(row.folder)}
                                className="flex items-center gap-2 min-w-0"
                              >
                                <FolderOpen className="h-4 w-4 text-blue-500 shrink-0" />
                                <span className="truncate">{row.folder.name}</span>
                              </button>
                            </td>
                            <td className="py-2 pr-4 text-right text-muted-foreground">—</td>
                            <td className="py-2 pr-4 text-muted-foreground">—</td>
                            <td className="py-2 text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setSharingFolder(row.folder)
                                    }}
                                  >
                                    <Share2 className="h-4 w-4 mr-2" />
                                    Share
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleRenameFolder(row.folder)
                                    }}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Rename
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleDeleteFolder(row.folder)
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                        )
                      }

                      const file = row.file
                      const isPdf = file.current_version?.mime_type === 'application/pdf'
                      const isImage = file.current_version?.mime_type?.startsWith('image/')
                      const isMarkdown = isMarkdownFile(file)
                      return (
                        <tr key={file.id} className="border-b hover:bg-muted/50">
                          <td className="py-2 pr-4">
                            <button
                              onClick={() => {
                                if (isPdf) setViewingPdf(file)
                                else if (isImage) void openImageLightbox(file, getCurrentOrgId())
                                else if (isMarkdown) setViewingMarkdown(file)
                              }}
                              className="text-left truncate max-w-[60vw]"
                              title={file.name}
                            >
                              {file.name}
                            </button>
                          </td>
                          <td className="py-2 pr-4 text-right text-muted-foreground">
                            {file.current_version ? formatBytes(file.current_version.size) : '—'}
                          </td>
                          <td className="py-2 pr-4 text-muted-foreground">{formatDateOnly(file.updated_at)}</td>
                          <td className="py-2 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => toggleFavorite(file)}>
                                  <Star className="h-4 w-4 mr-2" />
                                  {favoriteIds.has(file.id) ? 'Unfavorite' : 'Favorite'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDownload(file)}>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </DropdownMenuItem>
                                {file.current_version?.compression_alg && file.current_version.compression_alg !== 'none' && (
                                  <DropdownMenuItem onClick={() => handleDownloadCompressed(file)}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Compressed
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setSharingFile(file)}>
                                  <Share2 className="h-4 w-4 mr-2" />
                                  Share
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRename(file)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setMovingFile(file)}>
                                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                                  Move...
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setTaggingFile(taggingFile?.id === file.id ? null : file)}>
                                  <TagIcon className="h-4 w-4 mr-2" />
                                  Manage Tags
                                </DropdownMenuItem>
                                {taggingFile?.id === file.id && availableTags.length > 0 && (
                                  <div className="px-2 py-1.5 space-y-1 max-h-48 overflow-y-auto">
                                    {availableTags.map((tag: Tag) => {
                                      const isApplied = file.tags?.some((t: Tag) => t.id === tag.id)
                                      return (
                                        <button
                                          key={tag.id}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            if (isApplied) {
                                              handleRemoveTag(file, tag.id)
                                            } else {
                                              handleAddTag(file, tag.id)
                                            }
                                          }}
                                          className="flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-muted"
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color || '#6b7280' }} />
                                            <span>{tag.name}</span>
                                          </div>
                                          {isApplied && <Check className="h-4 w-4 text-primary" />}
                                        </button>
                                      )
                                    })}
                                  </div>
                                )}
                                {taggingFile?.id === file.id && availableTags.length === 0 && (
                                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No tags available. Create tags in Settings.</div>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setPendingDeleteFile(file)} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : viewMode === 'trading' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {sortedFiles.map((file) => {
                  const typeLabel = file.current_version?.mime_type || 'unknown'
                  const compression = file.current_version?.compression_alg || 'none'
                  const isImage = file.current_version?.mime_type?.startsWith('image/')
                  const isPdf = file.current_version?.mime_type === 'application/pdf'
                  const isMarkdown = isMarkdownFile(file)
                  return (
                    <div key={file.id} className="rounded-lg border bg-card overflow-hidden shadow-sm">
                      <div className="p-2">
                        <TradingCardThumb
                          file={file}
                          orgId={getCurrentOrgId()}
                          onOpenPdf={() => setViewingPdf(file)}
                          onOpenImage={() => {
                            if (isImage) void openImageLightbox(file, getCurrentOrgId())
                            else if (isPdf) setViewingPdf(file)
                          }}
                          onOpenMarkdown={() => {
                            if (isMarkdown) setViewingMarkdown(file)
                          }}
                        />
                      </div>
                      <div className="px-3 pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold leading-tight truncate" title={file.name}>
                              {file.name}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 truncate">
                              {typeLabel} • {compression}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => toggleFavorite(file)}
                              className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-muted"
                              title={favoriteIds.has(file.id) ? 'Unfavorite' : 'Favorite'}
                            >
                              <Star
                                className={`h-4 w-4 ${favoriteIds.has(file.id) ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`}
                              />
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuItem onClick={() => toggleFavorite(file)}>
                                  <Star className="h-4 w-4 mr-2" />
                                  {favoriteIds.has(file.id) ? 'Unfavorite' : 'Favorite'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleDownload(file)}>
                                  <Download className="h-4 w-4 mr-2" />
                                  Download
                                </DropdownMenuItem>
                                {file.current_version?.compression_alg && file.current_version.compression_alg !== 'none' && (
                                  <DropdownMenuItem onClick={() => handleDownloadCompressed(file)}>
                                    <Download className="h-4 w-4 mr-2" />
                                    Download Compressed
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => setSharingFile(file)}>
                                  <Share2 className="h-4 w-4 mr-2" />
                                  Share
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRename(file)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setMovingFile(file)}>
                                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                                  Move...
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setTaggingFile(taggingFile?.id === file.id ? null : file)}>
                                  <TagIcon className="h-4 w-4 mr-2" />
                                  Manage Tags
                                </DropdownMenuItem>
                                {taggingFile?.id === file.id && availableTags.length > 0 && (
                                  <div className="px-2 py-1.5 space-y-1 max-h-48 overflow-y-auto">
                                    {availableTags.map((tag: Tag) => {
                                      const isApplied = file.tags?.some((t: Tag) => t.id === tag.id)
                                      return (
                                        <button
                                          key={tag.id}
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            if (isApplied) {
                                              handleRemoveTag(file, tag.id)
                                            } else {
                                              handleAddTag(file, tag.id)
                                            }
                                          }}
                                          className="flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-muted"
                                        >
                                          <div className="flex items-center gap-2">
                                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color || '#6b7280' }} />
                                            <span>{tag.name}</span>
                                          </div>
                                          {isApplied && <Check className="h-4 w-4 text-primary" />}
                                        </button>
                                      )
                                    })}
                                  </div>
                                )}
                                {taggingFile?.id === file.id && availableTags.length === 0 && (
                                  <div className="px-2 py-1.5 text-sm text-muted-foreground">No tags available. Create tags in Settings.</div>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => setPendingDeleteFile(file)} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        <div className="mt-3 rounded-md border bg-muted/30 p-2 text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">Size</span>
                            <span>{file.current_version ? formatBytes(file.current_version.size) : '—'}</span>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-muted-foreground">Modified</span>
                            <span>{formatDate(file.updated_at)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : sortedFiles.length > 0 ? (
              <div className="divide-y overflow-hidden rounded-xl border bg-card">
                {sortedFiles.map((file) => {
                  const iconType = file.current_version
                    ? getFileIcon(file.current_version.mime_type)
                    : 'file'
                  const Icon = fileIcons[iconType] || File

                  const isPdf = file.current_version?.mime_type === 'application/pdf'
                  const isImage = file.current_version?.mime_type?.startsWith('image/')
                  const isMarkdown = isMarkdownFile(file)
                  const canInlineImage = !!isImage && !!(getCurrentOrgId() || file.org_id)

                  const effectiveOrgId = getCurrentOrgId() || file.org_id

                  return (
                    <div
                      key={file.id}
                      className={`flex items-center justify-between p-4 hover:bg-muted/50 transition-colors`}
                    >
                      <div
                        className={`flex items-center gap-3 flex-1 min-w-0 ${(isPdf || isImage || isMarkdown) ? 'cursor-pointer' : ''}`}
                        onClick={() => {
                          if (isPdf) setViewingPdf(file)
                          else if (isImage) void openImageLightbox(file, getCurrentOrgId())
                          else if (isMarkdown) setViewingMarkdown(file)
                        }}
                      >
                        {isPdf && effectiveOrgId ? (
                          <PDFThumbnail
                            orgId={effectiveOrgId}
                            fileId={file.id}
                            width={40}
                            height={52}
                            className="shrink-0"
                          />
                        ) : canInlineImage && effectiveOrgId ? (
                          <div className="h-[52px] w-[40px] rounded bg-muted border overflow-hidden shrink-0">
                            <InlineImageThumb file={file} orgId={effectiveOrgId} className="w-full h-full" />
                          </div>
                        ) : (
                          <Icon className={`h-8 w-8 shrink-0 ${fileIconColors[iconType] || 'text-muted-foreground'}`} />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{file.name}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm text-muted-foreground">
                              {file.current_version
                                ? (() => {
                                    const original = file.current_version.size
                                    const stored = file.current_version.stored_size
                                    const alg = file.current_version.compression_alg
                                    const storedSuffix =
                                      stored != null && alg && alg !== 'none' && stored > 0 && stored < original
                                        ? ` (stored ${formatBytes(stored)})`
                                        : ''
                                    return `${formatBytes(original)}${storedSuffix} • ${formatDate(file.updated_at)}`
                                  })()
                                : formatDate(file.updated_at)}
                            </p>
                            {file.tags && file.tags.length > 0 && (
                              <div className="flex items-center gap-1 flex-wrap">
                                {file.tags.map((tag) => (
                                  <span
                                    key={tag.id}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                                    style={{
                                      backgroundColor: tag.color ? `${tag.color}20` : '#6b728020',
                                      color: tag.color || '#6b7280',
                                      border: `1px solid ${tag.color || '#6b7280'}40`,
                                    }}
                                  >
                                    {tag.name}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onClick={() => toggleFavorite(file)}>
                            <Star className="h-4 w-4 mr-2" />
                            {favoriteIds.has(file.id) ? 'Unfavorite' : 'Favorite'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDownload(file)}>
                            <Download className="h-4 w-4 mr-2" />
                            Download
                          </DropdownMenuItem>
                          {file.current_version?.compression_alg && file.current_version.compression_alg !== 'none' && (
                            <DropdownMenuItem onClick={() => handleDownloadCompressed(file)}>
                              <Download className="h-4 w-4 mr-2" />
                              Download Compressed
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => setSharingFile(file)}>
                            <Share2 className="h-4 w-4 mr-2" />
                            Share
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRename(file)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setMovingFile(file)}>
                            <ArrowRightLeft className="h-4 w-4 mr-2" />
                            Move...
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setTaggingFile(taggingFile?.id === file.id ? null : file)}>
                            <TagIcon className="h-4 w-4 mr-2" />
                            Manage Tags
                          </DropdownMenuItem>
                          {taggingFile?.id === file.id && availableTags.length > 0 && (
                            <div className="px-2 py-1.5 space-y-1 max-h-48 overflow-y-auto">
                              {availableTags.map((tag: Tag) => {
                                const isApplied = file.tags?.some((t: Tag) => t.id === tag.id)
                                return (
                                  <button
                                    key={tag.id}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      if (isApplied) {
                                        handleRemoveTag(file, tag.id)
                                      } else {
                                        handleAddTag(file, tag.id)
                                      }
                                    }}
                                    className="flex items-center justify-between w-full px-2 py-1.5 text-sm rounded hover:bg-muted"
                                  >
                                    <div className="flex items-center gap-2">
                                      <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: tag.color || '#6b7280' }}
                                      />
                                      <span>{tag.name}</span>
                                    </div>
                                    {isApplied && <Check className="h-4 w-4 text-primary" />}
                                  </button>
                                )
                              })}
                            </div>
                          )}
                          {taggingFile?.id === file.id && availableTags.length === 0 && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No tags available. Create tags in Settings.
                            </div>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setPendingDeleteFile(file)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )
                })}
              </div>
            ) : (
              isFiltering ? (
                visibleFolders.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <Search className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">No matches</p>
                    <p className="text-sm">Nothing in this folder matches "{searchQuery.trim()}"</p>
                  </div>
                )
              ) : (
                folderContents.folders.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <FolderOpen className="h-16 w-16 mb-4 opacity-50" />
                    <p className="text-lg font-medium">No files yet</p>
                    <p className="text-sm">Upload files or create folders to get started</p>
                  </div>
                )
              )
            )}
          </div>
        </div>
      )}
      </div>

      {/* PDF Viewer Sheet */}
      <Sheet open={!!viewingPdf} onOpenChange={(open) => !open && setViewingPdf(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>{viewingPdf?.name || 'PDF Viewer'}</SheetTitle>
          </SheetHeader>
          {viewingPdf && getCurrentOrgId() && (
            <PDFViewer
              orgId={getCurrentOrgId()!}
              fileId={viewingPdf.id}
              fileName={viewingPdf.name}
              onDownload={() => handleDownload(viewingPdf)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Markdown Viewer Sheet */}
      <Sheet open={!!viewingMarkdown} onOpenChange={(open) => !open && setViewingMarkdown(null)}>
        <SheetContent side="right" className="w-full sm:max-w-2xl lg:max-w-4xl p-0">
          <SheetHeader className="sr-only">
            <SheetTitle>{viewingMarkdown?.name || 'Markdown Viewer'}</SheetTitle>
          </SheetHeader>
          {viewingMarkdown && (viewingMarkdown.org_id || getCurrentOrgId()) && (
            <MarkdownViewer
              orgId={viewingMarkdown.org_id || getCurrentOrgId()!}
              fileId={viewingMarkdown.id}
              fileName={viewingMarkdown.name}
              onDownload={() => handleDownload(viewingMarkdown)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Image Lightbox */}
      <Dialog
        open={!!viewingImage}
        onOpenChange={(open) => {
          if (!open) closeImageLightbox()
        }}
      >
        <DialogContent className="max-w-none w-[100vw] h-[100vh] p-0 bg-black border-none">
          <DialogHeader className="sr-only">
            <DialogTitle>{viewingImage?.name || 'Image preview'}</DialogTitle>
            <DialogDescription>Full-screen image preview</DialogDescription>
          </DialogHeader>
          <div className="absolute top-3 right-3 z-10">
            <Button variant="secondary" size="icon" onClick={closeImageLightbox}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="w-full h-full flex items-center justify-center">
            {viewingImageLoading ? (
              <div className="flex items-center gap-2 text-white">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Loading...</span>
              </div>
            ) : viewingImageError ? (
              <div className="text-white">{viewingImageError}</div>
            ) : viewingImageUrl ? (
              <img
                src={viewingImageUrl}
                alt={viewingImage?.name || 'image'}
                className="max-w-full max-h-full object-contain"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      {sharingFile && getCurrentOrgId() && (
        <ShareModal
          open={!!sharingFile}
          onOpenChange={(open) => !open && setSharingFile(null)}
          resourceType="file"
          resourceId={sharingFile.id}
          resourceName={sharingFile.name}
          orgId={getCurrentOrgId()!}
        />
      )}

      {sharingFolder && getCurrentOrgId() && (
        <ShareModal
          open={!!sharingFolder}
          onOpenChange={(open) => !open && setSharingFolder(null)}
          resourceType="folder"
          resourceId={sharingFolder.id}
          resourceName={sharingFolder.name}
          orgId={getCurrentOrgId()!}
        />
      )}

      <Dialog open={isMoveDialogOpen} onOpenChange={(open) => !open && setMovingFile(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move file</DialogTitle>
            <DialogDescription>
              Choose a destination folder for {movingFileName}.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-48">
                <Select value={moveScope} onValueChange={(v) => setMoveScope(v as any)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Destination" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="my-files">My Files</SelectItem>
                    <SelectItem value="org">Org</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {moveScope === 'team' && (
                <div className="flex-1 min-w-0">
                  <Select value={moveTeamId ?? ''} onValueChange={(v) => setMoveTeamId(v || null)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select team" />
                    </SelectTrigger>
                    <SelectContent>
                      {allTeams
                        .filter((t) => t.org.id === getCurrentOrgId())
                        .map((t) => (
                          <SelectItem key={t.team.id} value={t.team.id}>
                            {t.team.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 text-sm text-muted-foreground flex-wrap">
              {moveFolderPath.length > 0 ? (
                moveFolderPath.map((f, idx) => (
                  <span key={f.id} className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => loadMoveFolder(f.id)}
                      className="hover:text-foreground transition-colors"
                      disabled={moveLoading}
                    >
                      {idx === 0 ? getDomainName() : f.name}
                    </button>
                    {idx < moveFolderPath.length - 1 && <ChevronRight className="h-4 w-4" />}
                  </span>
                ))
              ) : (
                <span>Loading…</span>
              )}
            </div>

            <div className="border rounded-md max-h-72 overflow-y-auto">
              {moveLoading ? (
                <div className="p-4 text-sm text-muted-foreground">Loading folders…</div>
              ) : moveChildFolders.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">No subfolders</div>
              ) : (
                <div className="divide-y">
                  {moveChildFolders.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-muted/50"
                      onClick={() => loadMoveFolder(f.id)}
                    >
                      <FolderOpen className="h-4 w-4 text-blue-500" />
                      <span className="truncate">{f.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2">
              <div className="text-sm text-muted-foreground truncate">
                Destination: {moveFolderPath[moveFolderPath.length - 1]?.name || '—'}
              </div>
              <Button
                disabled={!movingFile || !moveFolderId || moveLoading}
                onClick={async () => {
                  const orgId = getMoveOrgId()
                  if (!movingFile || !moveFolderId || !orgId) return
                  try {
                    await api.files.move(orgId, movingFile.id, moveFolderId)
                    toast({ title: 'File moved' })
                    setMovingFile(null)
                    loadFolder()
                  } catch (e: any) {
                    toast({ title: e?.message || 'Failed to move file', variant: 'destructive' })
                  }
                }}
              >
                Move here
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingDeleteFile} onOpenChange={(open) => !open && setPendingDeleteFile(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete file</DialogTitle>
            <DialogDescription>
              This cannot be undone. Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{pendingDeleteFile?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteFile(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!pendingDeleteFile) return
                await handleDelete(pendingDeleteFile)
                setPendingDeleteFile(null)
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
